import { AuthorizationWrapper } from '@frontend/authorization';
import { AccountLogo } from '@frontend/components/common';
import type { ReactNode } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

import { ConfigurationLink, ConfigurationSection, configurationSections } from '../configuration/configuration-navigation.config';
import { getConfigurationSectionKey, useAdminNavbar } from './admin-navbar.controller';

interface AdminNavbarProps {
    className?: string;
}

export function AdminNavbar(props: AdminNavbarProps) {
    const viewProps = useAdminNavbar();

    return (
        <aside
            className={`h-dvh items-center justify-start ${props.className ?? ''}`}
            id='admin-sidenav'>
            <div className='bg-brand-primary w-1.5 self-stretch' />
            <nav className='bg-background-surface border-border-default z-50 flex h-full w-72 flex-col border-r'>
                <a
                    className='flex h-28 w-full items-center justify-center px-10'
                    href='/'
                    onClick={(event) => {
                        event.preventDefault();
                        viewProps.navigate('/');
                    }}>
                    <AccountLogo
                        accountId={viewProps.currentAccount}
                        fallbackSrc={viewProps.logo}
                    />
                </a>

                <div className='border-border-default flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto border-t p-4'>
                    {configurationSections.map((section) => (
                        <AuthorizedSection
                            key={getConfigurationSectionKey(section)}
                            section={section}>
                            <NavbarSection
                                section={section}
                                isOpen={viewProps.isSectionOpen(section)}
                                isActive={viewProps.isSectionActive(section)}
                                isLinkActive={viewProps.isLinkActive}
                                onToggle={() => viewProps.toggleSection(section)}
                                onNavigate={viewProps.navigate}
                            />
                        </AuthorizedSection>
                    ))}
                </div>
            </nav>
        </aside>
    );
}

export default AdminNavbar;

interface NavbarSectionProps {
    section: ConfigurationSection;
    isOpen: boolean;
    isActive: boolean;
    isLinkActive: (link: ConfigurationLink) => boolean;
    onToggle: () => void;
    onNavigate: (to: string) => void;
}

const NavbarSection = (props: NavbarSectionProps) => {
    const Icon = props.section.icon;

    return (
        <div className='flex flex-col'>
            <button
                className={`flex min-h-11 w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
                    props.isActive ? 'bg-brand-light text-brand-primary' : 'text-foreground-default hover:bg-background-default hover:text-brand-primary'
                }`}
                onClick={props.onToggle}
                type='button'>
                <span className='flex min-w-0 items-center gap-3'>
                    <Icon className='text-brand-primary size-5 shrink-0' />
                    <span className='truncate'>{props.section.label}</span>
                </span>
                {props.isOpen ? <LuChevronDown className='size-4 shrink-0' /> : <LuChevronRight className='size-4 shrink-0' />}
            </button>

            {props.isOpen && (
                <div className='border-border-default mt-1 ml-5 flex flex-col gap-1 border-l pl-3'>
                    {props.section.buttons.map((link) => (
                        <AuthorizedLink
                            key={link.to}
                            link={link}>
                            <button
                                className={`min-h-9 cursor-pointer rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                                    props.isLinkActive(link)
                                        ? 'bg-brand-primary text-background-surface'
                                        : 'text-brand-muted hover:bg-background-default hover:text-foreground-default'
                                }`}
                                onClick={() => props.onNavigate(link.to)}
                                type='button'>
                                {link.label}
                            </button>
                        </AuthorizedLink>
                    ))}
                </div>
            )}
        </div>
    );
};

const AuthorizedSection = (props: { section: ConfigurationSection; children: ReactNode }) => {
    if (!props.section.permissions) return <>{props.children}</>;

    return (
        <AuthorizationWrapper
            requiredPermissions={props.section.permissions}
            showLoading={false}>
            {props.children}
        </AuthorizationWrapper>
    );
};

const AuthorizedLink = (props: { link: ConfigurationLink; children: ReactNode }) => {
    if (!props.link.permissions) return <>{props.children}</>;

    return (
        <AuthorizationWrapper
            requiredPermissions={props.link.permissions}
            showLoading={false}>
            {props.children}
        </AuthorizationWrapper>
    );
};
